import moment from 'moment';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Icon,
  ICONS,
  Typography,
  TYPOGRAPHY_SIZE,
  PLATFORM_THEME_COLORS,
  PopoverTooltip,
  RadioInput,
} from '@thinknet-gmbh/platform-common-ui';
import { ROUTES } from 'src/components/app/app-routes.constants';
import {
  HeaderTitle,
  HeaderContainer,
  ElementContainer,
  PopoverInfoWrapper,
} from 'src/pages/gantt/gantt-calendar/gantt-view/components/gantt-popover/gantt-popover.styles';
import { useUpdateTimeStripe } from 'src/hooks/time-stripes/update-time-stripe.hook';
import { useDeleteRecordEntry } from 'src/hooks/gantts-records/delete-gantt-record.hook';
import { useGetProjectById } from 'src/hooks/projects/get-project-by-id.hook';
import { ActionIconWrapper } from './time-stripes-gantt-info.styles';
import { ITimeStripesGanttInfo } from './time-stripes-gantt-info.interface';

const TimeStripesGanttInfo = ({
  timeStripe,
  t,
  onClose,
  actionAllowed,
  openEditModal,
  openDeleteModal,
}: ITimeStripesGanttInfo) => {
  const getInitialStatus = () => {
    if (timeStripe.confirmed) {
      return 'confirmed';
    }
    if (timeStripe.flexible) {
      return 'flexible';
    }
    return 'planned';
  };

  const [status, setStatus] = useState<any>(getInitialStatus());

  const navigate = useNavigate();
  const { updateTimeStripe } = useUpdateTimeStripe();
  const { deleteRecordEntry } = useDeleteRecordEntry({ onSuccess: onClose });
  const { project } = useGetProjectById({ projectId: timeStripe.projectId });

  const task =
    project &&
    project.tasks &&
    Array.isArray(project.tasks) &&
    project.tasks.find((record: any) => {
      if (record.timeStripes && Array.isArray(record.timeStripes)) {
        return (
          record.timeStripes.find((stripe: { _id: string }) => stripe._id === timeStripe._id) !==
          undefined
        );
      }
      return false;
    });

  const deletingIsAllowed = task && task.timeStripes.length > 1;

  return (
    <PopoverInfoWrapper>
      <HeaderContainer>
        <HeaderTitle>
          <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
            {t('gantt:labels.timeStripeDetails')}
          </Typography>
        </HeaderTitle>

        <Icon
          icon={ICONS.xCloseIcon}
          rightMargin={false}
          size={22}
          color={PLATFORM_THEME_COLORS.grayVariations.murky}
          onClick={onClose}
        />
      </HeaderContainer>
      <ElementContainer>
        <Typography weight="bold">{t('projects:task.timeStripeName')}:</Typography>
        <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
          {timeStripe.stripeName}
        </Typography>
      </ElementContainer>
      <ElementContainer>
        <Typography weight="bold">{t('customers:labels.customerName')}:</Typography>
        <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
          {timeStripe.customer.deleted
            ? t('customers:labels.customerNameDeleted', { name: timeStripe.customer.name })
            : timeStripe.customer.name}
        </Typography>
      </ElementContainer>
      <ElementContainer>
        <Typography weight="bold">{t('projects:labels.startDate')}:</Typography>
        <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
          {moment(timeStripe.startDate).format('DD.MM.YYYY')}
        </Typography>
      </ElementContainer>
      <ElementContainer>
        <Typography weight="bold">{t('gantt:labels.planedHours')}:</Typography>
        <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
          {timeStripe.plannedHours} hr
        </Typography>
      </ElementContainer>
      <ElementContainer>
        <Typography weight="bold">{t('gantt:labels.status')}:</Typography>
        <RadioInput.Group
          className="status-group"
          buttonStyle="solid"
          onChange={e => {
            if (e.target.value === 'flexible') {
              updateTimeStripe({
                _id: timeStripe.source._id,
                confirmed: false,
                flexible: true,
              }).then(() => setStatus('flexible'));
            } else if (e.target.value === 'confirmed') {
              updateTimeStripe({
                _id: timeStripe.source._id,
                confirmed: true,
                flexible: false,
              }).then(() => setStatus('confirmed'));
            } else if (e.target.value === 'planned') {
              updateTimeStripe({
                _id: timeStripe.source._id,
                confirmed: false,
                flexible: false,
              }).then(() => setStatus('planned'));
            }
          }}
          value={status}
        >
          <RadioInput.Button
            style={{ height: '25px', display: 'flex', padding: '3px 15px', alignItems: 'center' }}
            value="planned"
          >
            {t('gantt:labels.planned')}
          </RadioInput.Button>
          <RadioInput.Button
            style={{ height: '25px', display: 'flex', padding: '3px 15px', alignItems: 'center' }}
            value="flexible"
          >
            {t('gantt:labels.flexible')}
          </RadioInput.Button>
          <RadioInput.Button
            style={{ height: '25px', display: 'flex', padding: '3px 15px', alignItems: 'center' }}
            disabled={!actionAllowed}
            value="confirmed"
          >
            {t('gantt:labels.confirmed')}
          </RadioInput.Button>
        </RadioInput.Group>
      </ElementContainer>
      <ElementContainer>
        <Typography weight="bold">{t('gantt:labels.action')}</Typography>
        <PopoverTooltip title={t('gantt:labels.timeStripeDetails')} placement="top">
          <ActionIconWrapper>
            <Icon
              size={18}
              onClick={() => actionAllowed && openEditModal()}
              icon={ICONS.editAlt}
              rightMargin={false}
              color={
                actionAllowed
                  ? PLATFORM_THEME_COLORS.primary.blue
                  : PLATFORM_THEME_COLORS.grayVariations.medium
              }
            />
          </ActionIconWrapper>
        </PopoverTooltip>
        <PopoverTooltip title={t('gantt:labels.goToProjectView')} placement="top">
          <ActionIconWrapper>
            <Icon
              size={18}
              onClick={() => navigate(`${ROUTES.project}/${timeStripe.projectId}`)}
              icon={ICONS.fileBlank}
              rightMargin={false}
              color={PLATFORM_THEME_COLORS.primary.blue}
            />
          </ActionIconWrapper>
        </PopoverTooltip>
        <PopoverTooltip title={t('gantt:labels.backToPool')} placement="top">
          <ActionIconWrapper>
            <Icon
              size={18}
              onClick={() =>
                actionAllowed && deleteRecordEntry(timeStripe.groupId, timeStripe.source._id)
              }
              icon={ICONS.chevronsLeft}
              rightMargin={false}
              color={PLATFORM_THEME_COLORS.tone.red}
            />
          </ActionIconWrapper>
        </PopoverTooltip>

        <PopoverTooltip title={t('gantt:labels.removeTimeStripe')} placement="top">
          <ActionIconWrapper>
            <Icon
              size={18}
              onClick={() => deletingIsAllowed && openDeleteModal()}
              icon={ICONS.trash}
              rightMargin={false}
              color={
                deletingIsAllowed
                  ? PLATFORM_THEME_COLORS.tone.red
                  : PLATFORM_THEME_COLORS.grayVariations.medium
              }
            />
          </ActionIconWrapper>
        </PopoverTooltip>
      </ElementContainer>
    </PopoverInfoWrapper>
  );
};

export default TimeStripesGanttInfo;
